import React from 'react';
import styled from 'styled-components';
import { convertToIndianWords } from '../utils/numberToWords';

interface SliderProps {
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 40px;
`;

const SliderTooltip = styled.div<{ position: number }>`
  position: absolute;
  top: 15px;
  left: ${(props) => props.position}%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  white-space: nowrap;
  z-index: 10;
  text-transform: capitalize;
`;

const StyledSlider = styled.input<{ value: number; min: number; max: number }>`
  position: relative;
  width: 100%;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  height: 6px;
  background: #ddd;
  border-radius: 2px;
  outline: none;

  &::-webkit-slider-runnable-track {
    background: linear-gradient(
      to right,
      #1a3156 ${(props) => ((props.value - props.min) / (props.max - props.min)) * 100}%,
      #ddd ${(props) => ((props.value - props.min) / (props.max - props.min)) * 100}%
    );
    border-radius: 2px;
    height: 6px;
  }

  /* Webkit (Chrome, Safari, newer versions of Opera) */
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 4px;
    height: 20px;
    background: #1a3156;
    border-radius: 2px;
    cursor: pointer;
    border: none;
    transform: translateY(-7px);
  }

  /* Firefox */
  &::-moz-range-thumb {
    width: 4px;
    height: 20px;
    background: #1a3156;
    border-radius: 2px;
    cursor: pointer;
    border: none;
    transform: translateY(-7px);
  }

  /* Firefox track */
  &::-moz-range-track {
    width: 4px;
    height: 20px;
    background: #ddd;
    border-radius: 2px;
    border: none;
  }

  &::-moz-range-progress {
    background: #1a3156;
    height: 6px;
    border-radius: 2px;
  }

  /* Edge and IE */
  &::-ms-thumb {
    width: 4px;
    height: 20px;
    background: #1a3156;
    border-radius: 0;
    cursor: pointer;
    border: none;
    transform: translateY(-6px);
  }

  /* Edge and IE track */
  &::-ms-track {
    width: 100%;
    height: 6px;
    background: transparent;
    border-color: transparent;
    color: transparent;
  }

  &::-ms-fill-lower {
    background: #1a3156;
    border-radius: 2px;
  }

  &::-ms-fill-upper {
    background: #ddd;
    border-radius: 2px;
  }
`;

export const Slider: React.FC<SliderProps> = ({ value, min, max, step, onChange }) => {
  const position = ((value - min) / (max - min)) * 100;

  return (
    <SliderContainer>
      <SliderTooltip position={position}>{convertToIndianWords(value)}</SliderTooltip>
      <StyledSlider type="range" min={min} max={max} step={step} value={value} onChange={onChange} />
    </SliderContainer>
  );
};
